import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type PublicDebtProfile = {
  slug: string;
  alias: string;
  story: string;
  poolId: number;
  poolName: string;
  remainingAmount: number;
};

export type AdminDebtProfile = {
  debtId: number;
  status: string;
  alias: string | null;
  story: string | null;
  proposedStory: string | null;
  consentedAt: string | null;
  poolName: string | null;
  balance: number;
};

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "include", ...init });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; message?: string };
    throw new Error(body.error || body.message || `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export function usePublicDebtProfiles() {
  return useQuery({
    queryKey: ["publicDebtProfiles"],
    queryFn: () => request<PublicDebtProfile[]>("/api/debt-profiles"),
  });
}

export function useAdminDebtProfiles(enabled: boolean) {
  return useQuery({
    queryKey: ["adminDebtProfiles"],
    queryFn: () => request<AdminDebtProfile[]>("/api/admin/debt-profiles"),
    enabled,
  });
}

export function usePublicProfileConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ debtId, consent, story }: { debtId: number; consent: boolean; story?: string }) =>
      request<{ status: string }>(`/api/debts/${debtId}/public-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ consent, ...(consent ? { story } : {}) }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["myDebts"] });
      void qc.invalidateQueries({ queryKey: ["publicDebtProfiles"] });
    },
  });
}

export function useReviewDebtProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ debtId, status, alias, story }: {
      debtId: number;
      status: "approved" | "rejected" | "hidden";
      alias?: string;
      story?: string;
    }) => request<{ status: string }>(`/api/admin/debt-profiles/${debtId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, ...(alias !== undefined ? { alias } : {}), ...(story !== undefined ? { story } : {}) }),
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["adminDebtProfiles"] });
      void qc.invalidateQueries({ queryKey: ["publicDebtProfiles"] });
    },
  });
}

export function useDesignatedCheckout() {
  return useMutation({
    mutationFn: ({ poolId, amount, profileSlug }: { poolId: number; amount: number; profileSlug: string }) =>
      request<{ url: string }>("/api/donations/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ poolId, amount, isRecurring: false, tosAgreed: true, profileSlug }),
      }),
  });
}